import { Timestamp } from 'firebase/firestore';

// Segment condition operators
export type SegmentOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'in'
  | 'not_in'
  | 'is_empty'
  | 'is_not_empty'
  | 'date_before'
  | 'date_after'
  | 'date_between'
  | 'days_ago'
  | 'has_any'
  | 'has_all'
  | 'has_none';

// Logic operators for combining conditions
export type LogicOperator = 'AND' | 'OR';

// Segment field types
export type SegmentFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'array'
  | 'email'
  | 'phone'
  | 'url'
  | 'select'
  | 'multiselect';

// Available segment fields
export interface SegmentField {
  id: string;
  name: string;
  label: string;
  type: SegmentFieldType;
  category: 'contact' | 'behavior' | 'ecommerce' | 'custom';
  description?: string;
  operators: SegmentOperator[];
  options?: { value: string; label: string }[]; // For select/multiselect fields
  isCustom?: boolean;
  isEcommerce?: boolean;
}

// Individual segment condition
export interface SegmentCondition {
  id: string;
  field: string;
  operator: SegmentOperator;
  value: string | string[] | number | Date | null;
  logic?: LogicOperator; // For combining with next condition
}

// Segment condition group
export interface SegmentConditionGroup {
  id: string;
  conditions: SegmentCondition[];
  logic: LogicOperator; // Logic between conditions in this group
  logicWithNext?: LogicOperator; // Logic with next group
}

// Main segment interface
export interface Segment {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  userId: string;
  isDynamic: boolean; // Dynamic segments auto-update, static are manual
  conditionGroups: SegmentConditionGroup[];
  contactCount: number;
  lastEvaluated?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isActive: boolean;
  tags?: string[]; // For organization
  color?: string; // For visual organization
}

// Segment template for quick creation
export interface SegmentTemplate {
  id: string;
  name: string;
  description: string;
  category: 'engagement' | 'demographics' | 'behavior' | 'ecommerce' | 'lifecycle' | 'disengaged';
  conditionGroups: SegmentConditionGroup[];
  isPopular?: boolean;
  icon?: string;
}

// Segment evaluation result
export interface SegmentEvaluationResult {
  segmentId: string;
  contactIds: string[];
  totalCount: number;
  evaluatedAt: Timestamp;
  evaluationTime: number; // milliseconds
}

// Segment analytics
export interface SegmentAnalytics {
  segmentId: string;
  period: '7d' | '30d' | '90d' | '1y';
  contactCount: number;
  growthRate: number; // percentage
  engagementRate: number; // average email engagement
  revenue?: number; // if ecommerce integration
  topTags: { tagId: string; count: number }[];
  topSources: { source: string; count: number }[];
  lastActivity: Timestamp;
}

// Ecommerce integration data
export interface EcommerceData {
  contactId: string;
  totalOrders: number;
  totalSpent: number;
  averageOrderValue: number;
  lastOrderDate?: Timestamp;
  firstOrderDate?: Timestamp;
  productCategories: string[];
  orderStatuses: string[];
  lifetimeValue: number;
  customerTier?: 'bronze' | 'silver' | 'gold' | 'platinum';
  preferredPaymentMethod?: string;
  shippingCountry?: string;
  lastAbandonedCart?: Timestamp;
  abandonedCartValue?: number;
}

// Segment filter for querying
export interface SegmentFilter {
  workspaceId?: string;
  userId?: string;
  isActive?: boolean;
  isDynamic?: boolean;
  tags?: string[];
  search?: string;
}

// Segment creation/update data
export interface SegmentFormData {
  name: string;
  description?: string;
  isDynamic: boolean;
  conditionGroups: SegmentConditionGroup[];
  tags?: string[];
  color?: string;
}

// Segment preview data
export interface SegmentPreview {
  segmentId?: string;
  conditionGroups: SegmentConditionGroup[];
  estimatedCount: number;
  sampleContacts: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    tags: string[];
  }[];
  evaluationTime: number;
}

// Segment import/export
export interface SegmentExport {
  segments: Segment[];
  exportedAt: Timestamp;
  exportedBy: string;
  version: string;
}

// Segment usage statistics
export interface SegmentUsage {
  segmentId: string;
  usedInCampaigns: number;
  usedInAutomations: number;
  lastUsed?: Timestamp;
  totalUses: number;
}

// Available segment fields configuration
export const AVAILABLE_SEGMENT_FIELDS: SegmentField[] = [
  // Contact fields
  {
    id: 'email',
    name: 'email',
    label: 'Email Address',
    type: 'email',
    category: 'contact',
    description: 'Contact email address',
    operators: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'in', 'not_in', 'is_empty', 'is_not_empty']
  },
  {
    id: 'firstName',
    name: 'firstName',
    label: 'First Name',
    type: 'text',
    category: 'contact',
    description: 'Contact first name',
    operators: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty']
  },
  {
    id: 'lastName',
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
    category: 'contact',
    description: 'Contact last name',
    operators: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty']
  },
  {
    id: 'phone',
    name: 'phone',
    label: 'Phone Number',
    type: 'phone',
    category: 'contact',
    description: 'Contact phone number',
    operators: ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty']
  },
  {
    id: 'status',
    name: 'status',
    label: 'Subscription Status',
    type: 'select',
    category: 'contact',
    description: 'Email subscription status',
    operators: ['equals', 'not_equals', 'in', 'not_in'],
    options: [
      { value: 'subscribed', label: 'Subscribed' },
      { value: 'unsubscribed', label: 'Unsubscribed' },
      { value: 'pending', label: 'Pending' }
    ]
  },
  {
    id: 'tags',
    name: 'tags',
    label: 'Tags',
    type: 'array',
    category: 'contact',
    description: 'Contact tags',
    operators: ['has_any', 'has_all', 'has_none', 'is_empty', 'is_not_empty']
  },
  {
    id: 'addedAt',
    name: 'addedAt',
    label: 'Date Added',
    type: 'date',
    category: 'contact',
    description: 'When contact was added',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago']
  },
  {
    id: 'lastChanged',
    name: 'lastChanged',
    label: 'Last Updated',
    type: 'date',
    category: 'contact',
    description: 'When contact was last updated',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago']
  },
  {
    id: 'source',
    name: 'source',
    label: 'Source',
    type: 'select',
    category: 'contact',
    description: 'How contact was added',
    operators: ['equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty'],
    options: [
      { value: 'form', label: 'Signup Form' },
      { value: 'import', label: 'Import' },
      { value: 'manual', label: 'Manual' },
      { value: 'api', label: 'API' },
      { value: 'shopify', label: 'Shopify' },
      { value: 'woocommerce', label: 'WooCommerce' }
    ]
  },

  // Behavioral fields
  {
    id: 'totalEmailsSent',
    name: 'totalEmailsSent',
    label: 'Total Emails Sent',
    type: 'number',
    category: 'behavior',
    description: 'Total number of emails sent to contact',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal']
  },
  {
    id: 'totalEmailsOpened',
    name: 'totalEmailsOpened',
    label: 'Total Emails Opened',
    type: 'number',
    category: 'behavior',
    description: 'Total number of emails opened by contact',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal']
  },
  {
    id: 'totalEmailsClicked',
    name: 'totalEmailsClicked',
    label: 'Total Emails Clicked',
    type: 'number',
    category: 'behavior',
    description: 'Total number of emails clicked by contact',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal']
  },
  {
    id: 'lastEmailSent',
    name: 'lastEmailSent',
    label: 'Last Email Sent',
    type: 'date',
    category: 'behavior',
    description: 'Date of last email sent to contact',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty']
  },
  {
    id: 'lastEmailOpened',
    name: 'lastEmailOpened',
    label: 'Last Email Opened',
    type: 'date',
    category: 'behavior',
    description: 'Date of last email opened by contact',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty']
  },
  {
    id: 'lastEmailClicked',
    name: 'lastEmailClicked',
    label: 'Last Email Clicked',
    type: 'date',
    category: 'behavior',
    description: 'Date of last email clicked by contact',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty']
  },

  // Ecommerce fields (when integrated)
  {
    id: 'ecommerce.totalOrders',
    name: 'ecommerce.totalOrders',
    label: 'Total Orders',
    type: 'number',
    category: 'ecommerce',
    description: 'Total number of orders placed',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.totalSpent',
    name: 'ecommerce.totalSpent',
    label: 'Total Spent',
    type: 'number',
    category: 'ecommerce',
    description: 'Total amount spent by customer',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.averageOrderValue',
    name: 'ecommerce.averageOrderValue',
    label: 'Average Order Value',
    type: 'number',
    category: 'ecommerce',
    description: 'Average value per order',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.lastOrderDate',
    name: 'ecommerce.lastOrderDate',
    label: 'Last Order Date',
    type: 'date',
    category: 'ecommerce',
    description: 'Date of last order placed',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.customerTier',
    name: 'ecommerce.customerTier',
    label: 'Customer Tier',
    type: 'select',
    category: 'ecommerce',
    description: 'Customer loyalty tier',
    operators: ['equals', 'not_equals', 'in', 'not_in'],
    options: [
      { value: 'bronze', label: 'Bronze' },
      { value: 'silver', label: 'Silver' },
      { value: 'gold', label: 'Gold' },
      { value: 'platinum', label: 'Platinum' }
    ],
    isEcommerce: true
  },
  {
    id: 'ecommerce.productCategories',
    name: 'ecommerce.productCategories',
    label: 'Product Categories',
    type: 'array',
    category: 'ecommerce',
    description: 'Categories of products purchased',
    operators: ['has_any', 'has_all', 'has_none', 'is_empty', 'is_not_empty'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.lastAbandonedCart',
    name: 'ecommerce.lastAbandonedCart',
    label: 'Last Abandoned Cart',
    type: 'date',
    category: 'ecommerce',
    description: 'Date of last abandoned cart',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.lastCheckoutStarted',
    name: 'ecommerce.lastCheckoutStarted',
    label: 'Last Checkout Started',
    type: 'date',
    category: 'ecommerce',
    description: 'Date of last checkout attempt',
    operators: ['date_before', 'date_after', 'date_between', 'days_ago', 'is_empty', 'is_not_empty'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.storeVisits',
    name: 'ecommerce.storeVisits',
    label: 'Store Visits',
    type: 'number',
    category: 'ecommerce',
    description: 'Number of store visits',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
    isEcommerce: true
  },
  {
    id: 'ecommerce.purchasedDuringSale',
    name: 'ecommerce.purchasedDuringSale',
    label: 'Purchased During Sale',
    type: 'boolean',
    category: 'ecommerce',
    description: 'Whether customer purchased during a sale',
    operators: ['equals', 'not_equals'],
    isEcommerce: true
  },
  {
    id: 'customFields.birthday',
    name: 'birthday',
    label: 'Birthday',
    type: 'date',
    category: 'custom',
    description: 'Contact birthday',
    operators: ['equals', 'not_equals', 'date_before', 'date_after', 'date_between', 'days_ago'],
    isCustom: true
  }
];

// Popular segment templates
export const SEGMENT_TEMPLATES: SegmentTemplate[] = [
  {
    id: 'high-engagement',
    name: 'High Engagement',
    description: 'Contacts who frequently open and click emails',
    category: 'engagement',
    isPopular: true,
    icon: '📈',
    conditionGroups: [
      {
        id: 'engagement-group',
        conditions: [
          {
            id: 'open-rate',
            field: 'totalEmailsOpened',
            operator: 'greater_than',
            value: 5,
            logic: 'AND'
          },
          {
            id: 'click-rate',
            field: 'totalEmailsClicked',
            operator: 'greater_than',
            value: 2
          }
        ],
        logic: 'AND'
      }
    ]
  },
  {
    id: 'new-subscribers',
    name: 'New Subscribers',
    description: 'Contacts added within the last 30 days',
    category: 'lifecycle',
    isPopular: true,
    icon: '🆕',
    conditionGroups: [
      {
        id: 'new-subscribers-group',
        conditions: [
          {
            id: 'recent-signup',
            field: 'addedAt',
            operator: 'date_after',
            value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
          }
        ],
        logic: 'AND'
      }
    ]
  },
  {
    id: 'inactive-subscribers',
    name: 'Inactive Subscribers',
    description: 'Contacts who haven\'t opened emails in 90 days',
    category: 'engagement',
    isPopular: true,
    icon: '😴',
    conditionGroups: [
      {
        id: 'inactive-group',
        conditions: [
          {
            id: 'no-recent-opens',
            field: 'lastEmailOpened',
            operator: 'days_ago',
            value: 90
          }
        ],
        logic: 'AND'
      }
    ]
  },
  {
    id: 'high-value-customers',
    name: 'High Value Customers',
    description: 'Customers who have spent over $500',
    category: 'ecommerce',
    isPopular: true,
    icon: '💎',
    conditionGroups: [
      {
        id: 'high-value-group',
        conditions: [
          {
            id: 'high-spend',
            field: 'ecommerce.totalSpent',
            operator: 'greater_than',
            value: 500
          }
        ],
        logic: 'AND'
      }
    ]
  },
  {
    id: 'frequent-buyers',
    name: 'Frequent Buyers',
    description: 'Customers with 5+ orders',
    category: 'ecommerce',
    isPopular: true,
    icon: '🛒',
    conditionGroups: [
      {
        id: 'frequent-buyers-group',
        conditions: [
          {
            id: 'many-orders',
            field: 'ecommerce.totalOrders',
            operator: 'greater_than_or_equal',
            value: 5
          }
        ],
        logic: 'AND'
      }
    ]
  },
  {
    id: 'abandoned-cart',
    name: 'Abandoned Cart',
    description: 'Customers with abandoned carts in last 7 days',
    category: 'ecommerce',
    isPopular: true,
    icon: '🛍️',
    conditionGroups: [
      {
        id: 'abandoned-cart-group',
        conditions: [
          {
            id: 'recent-abandonment',
            field: 'ecommerce.lastAbandonedCart',
            operator: 'days_ago',
            value: 7
          }
        ],
        logic: 'AND'
      }
    ]
  }
];
